import React, { useEffect, useMemo, useState } from 'react';
import { ComposableDeviceFormProps } from '../../../../../components/ComposableDevice/ComposableDeviceForm/ComposableDeviceForm';
import { getMachineTypeSelector } from '../../../../../redux/composableDevice/selectors/composableDeviceSelectors';
import {
  useAppDispatch,
  useAppSelector,
} from '../../../../../redux/store.model';
import { Box, Button, MenuItem, TextField, Typography } from '@mui/material';
import { ErrorText } from '../../../../../theme/components/Forms';
import { getPolyglot } from '../../../../../i18n';
import { useControlUnitsByIds } from '../../../../../redux/controlUnit/hooks/useControlUnitsByIds';
import { getAllAvailableDevicesToAddByGroupIdSelector } from '../../../../../redux/commander/selectors';
import { isNotNullOrUndefined } from '../../../../../util/types/Array';
import { getControlUnitsActiveByIdFilter } from '../../../../../redux/controlUnit/selectors';
import { ChannelMap } from '../../../../../redux/groups/api/group.model';
import TextFieldFile from '../../../../../components/commons/TextFieldFile';
import { IconUpload } from '../../../../../redux/groups/groups.model';
import { useShowErrorMessage } from '../../../../../handlingErrors';
import { deviceButtonsRightsSelector } from '../../../../../redux/commander/selectors/devicesRightsSelectors';
import { getChannelsByComposableDevice } from '../../../util/getChannelsByComposableDevice';
import { deleteGroupIcon } from '../../../../../redux/groups/actions/thunks';

const allowedImageTypes = ['image/png', 'image/jpeg', 'image/png'];
const MAX_SIZE_IMAGE_KBYTE = 8192;
const KBYTE = 1024;
const maxHeightImage = 1200; // 1200px
const maxWidthImage = 1200; // 1200px
export const ComposableDeviceFormC33R_CH = (
  props: ComposableDeviceFormProps
) => {
  const { formik } = props;

  formik.values.composableDevice = formik.values.composableDevice || {
    channels: [],
    labels: {},
    machineTypeId: '',
  };

  const { data: devicesControlUnits } = useControlUnitsByIds({
    deviceIds: formik.values.devices,
  });

  const showError = useShowErrorMessage();
  const showDeviceInfo = useAppSelector(
    (state) =>
      deviceButtonsRightsSelector(state, {
        deviceId: formik.values.devices?.[0],
        groupId: formik.values.parentGroupID,
      }).showDeviceInfo
  );

  const [channels, setChannels] = useState<ChannelMap[]>([]);
  const devices = useAppSelector((state) =>
    getAllAvailableDevicesToAddByGroupIdSelector(state, {
      groupId: formik.values.parentGroupID,
    })
  );
  const machineType = useAppSelector((state) =>
    getMachineTypeSelector(state, {
      machineTypeId: formik.values.composableDevice?.machineTypeId,
    })
  );

  const displayForm = useMemo(
    () =>
      devicesControlUnits
        .map((device) => device.controlUnits)
        .filter(isNotNullOrUndefined).length !== 0,
    [devicesControlUnits]
  );

  const devicesValue =
    typeof formik.values?.devices === 'object' ? formik.values?.devices : [];

  const selectedDevice = devices.find(
    (device) => device.uuid === (formik.values.devices?.[0] || '')
  );
  const default_channel =
    machineType?.labels['merchandiser_default_channel'] || '';
  const defaultImage = machineType?.labels['default_image'] || '';

  const composableDeviceLabels = {
    serial_number:
      formik.values.composableDevice.labels?.serial_number ||
      selectedDevice?.uuid ||
      '',
    default_channel:
      formik.values.composableDevice.labels?.default_channel || default_channel,
    imageURL: formik.values.composableDevice.labels?.imageURL || defaultImage,
  };

  const polyglot = getPolyglot();

  const actionsChannelsIndex =
    machineType?.labels['merchandise_channel_actions']?.split('-') || [];
  const [initialActionsChannels, _endActionsChannels] =
    actionsChannelsIndex.map((value) => parseInt(value, 10));

  const eventsChannelsIndex =
    machineType?.labels['merchandise_channel_events']?.split('-') || [];
  const [initialEventChannels, endEventChannels] = eventsChannelsIndex.map(
    (value) => parseInt(value, 10)
  );

  const sensorChannelsIndex =
    machineType?.labels['merchandise_channel_sensors']?.split('-') || [];
  const [initialSensorChannels, endSensorChannels] = sensorChannelsIndex.map(
    (value) => parseInt(value, 10)
  );

  const virtualChannelsIndex =
    machineType?.labels['merchandise_channel_virtual_sensors']?.split('-') ||
    [];
  const [initialVirtualChannels, endVirtualChannels] = virtualChannelsIndex.map(
    (value) => parseInt(value, 10)
  );
  const imagePreview =
    (props.logo?.imagePreviewUrl !== ''
      ? props.logo?.imagePreviewUrl
      : undefined) ||
    formik.values.logoURL ||
    formik.values.imageURL ||
    formik.values.composableDevice.labels?.imageURL ||
    machineType?.labels['default_image'];

  const removeImage = useAppDispatch();
  const deviceControlUnits = (
    devicesControlUnits.find(
      (device) => device.deviceId === selectedDevice?.uuid
    )?.controlUnits || []
  ).filter(getControlUnitsActiveByIdFilter);

  const defaultChannelValues = useMemo(
    () =>
      Object.entries(machineType?.labels ?? {}).filter(([key]) => {
        const numberKey = parseInt(key.split('.')[1], 10);

        const isSensor =
          key.endsWith('name') &&
          numberKey >= initialSensorChannels &&
          numberKey <= endSensorChannels;

        return isSensor;
      }),
    [initialSensorChannels, endSensorChannels, machineType]
  );

  const channelList = getChannelsByComposableDevice(
    machineType!,
    deviceControlUnits,
    selectedDevice?.uuid || ''
  );

  const setDefaultChannels = (updateChannelInfo: boolean) => {
    const newChannels: ChannelMap[] = [];
    for (let i = initialActionsChannels; i <= endVirtualChannels; i++) {
      const defaultInfo = formik.values.composableDevice?.channels?.[i];
      if (defaultInfo && !updateChannelInfo) {
        newChannels.push(defaultInfo);
        continue;
      }
      if (!deviceControlUnits.length) {
        continue;
      }

      newChannels.push(channelList[i]);
    }

    formik.setFieldValue(`composableDevice.channels`, newChannels);
    setChannels(newChannels);
  };

  const updateLabels = () => {
    formik.setFieldValue('composableDevice.labels', composableDeviceLabels);
  };

  useEffect(() => {
    setDefaultChannels(false);
  }, [formik.values.devices, devicesControlUnits]);

  useEffect(() => {
    formik.setFieldValue(
      'composableDevice.labels',
      formik.values.composableDevice?.labels || composableDeviceLabels
    );
  }, [selectedDevice]);

  const handleImageChange = (
    file: React.ChangeEvent<HTMLInputElement>
  ): Promise<IconUpload | null> => {
    return new Promise((resolve) => {
      const iconFile = file.target.files && file.target.files[0];
      if (iconFile) {
        if (
          iconFile.type.indexOf('image') === -1 ||
          allowedImageTypes.indexOf(iconFile.type) === -1
        ) {
          /* Snackbar message error */
          const message = {
            header: '',
            text: polyglot.t('error.only_format_images'),
            type: 'error',
          };
          showError(message.text);
          resolve(null);
        }
        if (iconFile.size > MAX_SIZE_IMAGE_KBYTE * KBYTE) {
          const message = {
            header: '',
            text: `${polyglot.t(
              'error.images_size_restriction'
            )} ${MAX_SIZE_IMAGE_KBYTE} ${polyglot.t('general.KB')}`,
            type: 'error',
          };
          showError(message.text);
          resolve(null);
        }
        const URL = window.URL || window.webkitURL;
        const img = new Image();
        img.src = URL.createObjectURL(iconFile);
        img.onload = () => {
          if (img.width > maxWidthImage) {
            const message = {
              text: `${polyglot.t(
                'error.images_width_restriction'
              )} ${polyglot.t(
                'general.max_width'
              )}: ${maxWidthImage} ${polyglot.t('general.px')}`,
              type: 'error',
            };
            showError(message.text);
            resolve(null);
          } else if (img.height > maxHeightImage) {
            const message = {
              text: `${polyglot.t(
                'error.images_height_restriction'
              )} ${polyglot.t(
                'general.max_height'
              )}: ${maxHeightImage} ${polyglot.t('general.px')}`,
              type: 'error',
            };
            showError(message.text);
            resolve(null);
          }
        };
        const fileReader = new FileReader();
        fileReader.onloadend = () => {
          const imageString = fileReader.result as string;
          resolve({
            fileName: iconFile.name,
            toolTip: `upload file ${iconFile.name}`,
            file: iconFile,
            imagePreviewUrl: imageString,
          });
        };
        fileReader.readAsDataURL(iconFile);
      } else {
        showError(polyglot.t('composable_device.failed_to_load'));
        resolve(null);
      }
    });
  };
  const resetIcon = async () => {
    if (formik.values.id) {
      try {
        await removeImage(deleteGroupIcon(formik.values.id));
        formik.setFieldValue('logoURL', '');
        formik.setFieldValue('iconURL', '');
        formik.setFieldValue(
          'composableDevice.labels.imageURL',
          machineType?.labels['default_image'] || ''
        );
        props.setLogo(null);
        props.setImage(null);
        formik.submitForm();
      } catch (error) {
        showError(polyglot.t('composable_device.failed_to_delete'));
      }
    }
  };

  return (
    <>
      <TextField
        name="name"
        type="name"
        label={polyglot.t('composable_device.name')}
        onChange={formik.handleChange}
        value={formik.values.name}
        defaultValue={formik.initialValues.name}
        required
        error={formik.touched.name && Boolean(formik.errors.name)}
        helperText={
          formik.touched.name &&
          formik.errors.name && (
            <ErrorText>{polyglot.t(formik.errors.name)}</ErrorText>
          )
        }
      />
      <Box
        display={'grid'}
        gridTemplateColumns={'20% 1fr'}
        gap={4}
        alignItems={'center'}
      >
        {imagePreview ? (
          <Box
            component="img"
            sx={{
              mr: 1,
              verticalAlign: 'middle',
              maxHeight: '255px',
              objectFit: 'contain',
              maxWidth: '100%',
            }}
            src={imagePreview}
            alt="added model"
          ></Box>
        ) : null}
        <Box
          display={'flex'}
          width={'100%'}
          flexDirection={'column'}
          alignItems={'center'}
        >
          <TextFieldFile
            sx={{ width: '100%' }}
            id={`logoURL-${formik.values.id}`}
            name="logoURL"
            label={polyglot.t('composable_device.logo_url')}
            onChange={async (e: React.ChangeEvent<HTMLInputElement>) => {
              const loadedLogo = await handleImageChange(e);
              if (loadedLogo) {
                formik.setFieldValue('logoURL', loadedLogo?.imagePreviewUrl);
                props.setLogo(loadedLogo);
              } else {
                formik.setFieldValue('logoURL', imagePreview);
                props.setLogo(null);
                formik.setFieldError(
                  'imageURL',
                  polyglot.t('composable_device.failed_to_load')
                );
              }
            }}
            resetIcon={props.logo?.imagePreviewUrl === ''}
            value={imagePreview}
            error={Boolean(formik.errors.logoURL)}
            helperText={
              formik.errors.logoURL
                ? null //imageError
                : `${polyglot.t(
                    'group.max_size'
                  )}: ${MAX_SIZE_IMAGE_KBYTE} ${polyglot.t('general.KB')}`
            }
          />
          {formik.values.imageURL && (
            <Button type="button" variant="text" onClick={resetIcon}>
              {polyglot.t('group.delete_icon')}
            </Button>
          )}
        </Box>
      </Box>
      <TextField
        id="devices-select"
        select
        label={polyglot.t('composable_device.navigation_tooltip')}
        disabled={true}
        value={devicesValue[0] ?? null}
        onChange={(event) => {
          formik.setFieldValue(
            'devices',
            event.target.value ? [event.target.value] : []
          );
        }}
        onBlur={formik.handleBlur}
        SelectProps={{
          id: 'devices',
          name: 'devices',
        }}
      >
        {devices.map((option) => (
          <MenuItem key={option.uuid} value={option.uuid}>
            {option.name}
          </MenuItem>
        ))}
      </TextField>
      {formik.errors.devices && <ErrorText>{formik.errors.devices}</ErrorText>}

      {displayForm ? (
        <>
          <>
            {Object.entries(composableDeviceLabels)
              .filter(([key]) => key !== 'imageURL')
              .map(([key, value]) => {
                if (key === 'default_channel') {
                  return (
                    <TextField
                      key={key}
                      label={polyglot.t(
                        `composable_device.default_standard_view_module`
                      )}
                      select
                      value={
                        defaultChannelValues.find(
                          ([key]) => key === value
                        )?.[1] || ''
                      }
                      onChange={(event) => {
                        const foundChannel = defaultChannelValues.find(
                          ([, label]) => label === event.target.value
                        );
                        if (foundChannel) {
                          formik.setFieldValue(
                            `composableDevice.labels.${key}`,
                            foundChannel[0]
                          );
                        }
                      }}
                      onBlur={formik.handleBlur}
                      sx={{
                        width: '100%',
                      }}
                    >
                      {defaultChannelValues.map(([key, label]) => (
                        <MenuItem key={key} value={label}>
                          {label}
                        </MenuItem>
                      ))}
                    </TextField>
                  );
                }

                const fieldValue =
                  formik.values.composableDevice?.labels?.[key] || '';
                return (
                  <TextField
                    name="serial_number"
                    label={polyglot.t(`composable_device.${key}`)}
                    onChange={(event) => {
                      formik.setFieldValue(
                        `composableDevice.labels.${key}`,
                        event.target.value
                      );
                    }}
                    value={fieldValue}
                    required
                    key={key}
                  />
                );
              })}
          </>
          {showDeviceInfo ? (
            <>
              <Button
                size="medium"
                variant="text"
                sx={{ alignSelf: 'end' }}
                onClick={() => {
                  setDefaultChannels(true);
                  updateLabels();
                }}
              >
                {polyglot.t('composable_device.reset_default_channels')}
              </Button>
              {channels
                .filter(
                  (channel) => channel.channelType !== 'virtual_superheat'
                )
                .map((channel, index) => {
                  return (
                    <Box key={index}>
                      <Typography variant="subtitle1">
                        {machineType?.labels[`channels.${index}.name`]}
                      </Typography>
                      <Box sx={{ display: 'flex' }}>
                        <TextField
                          select
                          sx={{ width: '100%' }}
                          key={index}
                          label={'channel'}
                          name={channel.channel}
                          type="name"
                          value={channel.channel || ''}
                          onChange={(event) => {
                            const selectedChannel = deviceControlUnits.find(
                              (cu) => cu.id === event.target.value
                            );

                            const selected =
                              index < initialSensorChannels
                                ? selectedChannel?.events.find(
                                    (c) => c.id === event.target.value
                                  )
                                : selectedChannel?.channels.find(
                                    (c) => c.id === event.target.value
                                  );

                            formik.setFieldValue(
                              `composableDevice.channels.${index}.channel`,
                              selected ? selected.id : null
                            );
                          }}
                          required
                        >
                          {index < initialEventChannels
                            ? deviceControlUnits
                                .find((cu) => cu.id === channel.controlUnitId)
                                ?.actions.map((cu) => (
                                  <MenuItem key={cu.id} value={cu.id}>
                                    {cu.id}
                                  </MenuItem>
                                ))
                            : index < initialSensorChannels
                            ? deviceControlUnits
                                .find((cu) => cu.id === channel.controlUnitId)
                                ?.events.map((cu) => (
                                  <MenuItem key={cu.id} value={cu.id}>
                                    {cu.id}
                                  </MenuItem>
                                ))
                            : deviceControlUnits
                                .find((cu) => cu.id === channel.controlUnitId)
                                ?.channels.map((cu) => (
                                  <MenuItem key={cu.id} value={cu.id}>
                                    {cu.name}
                                  </MenuItem>
                                ))}
                        </TextField>
                      </Box>
                    </Box>
                  );
                })}
            </>
          ) : null}
        </>
      ) : null}
    </>
  );
};
